import { useEffect, useState } from "react";
import { FormDialog, Field } from "./dreamShared";
import { MELT_INPUT, MELT_TEXTAREA } from "./ui/melt";

export type ContactStatus = "to-reach-out" | "reached-out" | "talked";

export type ContactPerson = {
  id: string;
  name: string;
  role?: string;
  linkedin?: string;
  email?: string;
  status: ContactStatus;
  notes?: string;
};

type AddContactPersonProps = {
  isOpen: boolean;
  companyName: string;
  editPerson?: ContactPerson | null;
  onClose: () => void;
  onSave: (person: ContactPerson) => void;
};

export default function AddContactPerson({
  isOpen,
  companyName,
  editPerson,
  onClose,
  onSave,
}: AddContactPersonProps) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (editPerson) {
      setName(editPerson.name);
      setRole(editPerson.role || "");
      setLinkedin(editPerson.linkedin || "");
      setEmail(editPerson.email || "");
      setNotes(editPerson.notes || "");
    } else {
      setName("");
      setRole("");
      setLinkedin("");
      setEmail("");
      setNotes("");
    }
  }, [editPerson, isOpen]);

  const handleSubmit = () => {
    const person: ContactPerson = {
      id: editPerson?.id || Date.now().toString(),
      name: name.trim(),
      status: editPerson?.status || "to-reach-out",
    };
    if (role.trim()) person.role = role.trim();
    if (linkedin.trim()) person.linkedin = linkedin.trim();
    if (email.trim()) person.email = email.trim();
    if (notes.trim()) person.notes = notes.trim();

    onSave(person);
    onClose();
  };

  return (
    <FormDialog
      open={isOpen}
      onClose={onClose}
      title={editPerson ? "Edit person" : "Add person"}
      description={`Someone you know, or want to know, at ${companyName}.`}
      submitLabel={editPerson ? "Save changes" : "Add person"}
      canSubmit={Boolean(name.trim())}
      onSubmit={handleSubmit}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field id="person-name" label="Name" required>
          <input
            id="person-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sara Lin"
            className={MELT_INPUT}
            autoFocus
          />
        </Field>
        <Field id="person-role" label="Title or how you know them">
          <input
            id="person-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="e.g. PM · school alum"
            className={MELT_INPUT}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field id="person-linkedin" label="LinkedIn">
          <input
            id="person-linkedin"
            value={linkedin}
            onChange={(e) => setLinkedin(e.target.value)}
            placeholder="linkedin.com/in/…"
            className={MELT_INPUT}
          />
        </Field>
        <Field id="person-email" label="Email">
          <input
            id="person-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            className={MELT_INPUT}
          />
        </Field>
      </div>

      <Field id="person-notes" label="Notes">
        <textarea
          id="person-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Shared interests, what to ask, intro path…"
          rows={3}
          className={MELT_TEXTAREA}
        />
      </Field>
    </FormDialog>
  );
}
